import { BellOff, Check, Search } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import {
  ActionKey,
  Button,
  Chip,
  HoldToConfirm,
  Input,
  PageHeader,
  Switch,
  TableSkeleton,
  Toolbar,
  cn,
  useToast,
  type EntityKind,
} from '@/components/ui';
import { EntityLink } from '@/components/layout/workspace/EntityLink';
import { ObjectLink } from '@/components/layout/workspace/ObjectLink';
import { useObjectOpen } from '@/components/layout/workspace/useObjectOpen';
import { useObserverAnchor } from '@/components/observer/useObserverAnchor';
import { problems as seed, severityLabels, severityPill } from '@/pages/design/showcase/fixtures';
import { matchesQuery } from '@/lib/searchText';
import { at } from '../../model';
import { usePrefs, useViewLoading } from '../../prefs';
import { Body, Page } from '../common';
import { ARRIVALS } from './data';

/*
  Проблемы — живой список приложения-образца. Здесь собраны приёмы характера:
  - новое приезжает сверху с подсветкой края и не сдвигает то, что читают (R35);
  - закрытая проблема остывает, а не исчезает молча (R34);
  - у действий клавиши проступают по Alt (R33): J/K — по строкам, A — подтвердить, S — заглушить;
  - подтвердить и заглушить — сразу и с «Отменить» (R39), закрыть — удержанием (R27);
  - имена живые: карточка по наведению, вкладка по клику (R26, R37);
  - пока список едет, на его месте сетка той же формы (R31).
*/

interface Problem {
  id: string;
  sev: number;
  name: string;
  target: { kind: EntityKind; name: string };
  since: string;
  acked: boolean;
  muted?: boolean;
  /** Только что приехала — подсветка края один раз. */
  fresh?: boolean;
  /** Закрыта — остывает и уходит. */
  closed?: boolean;
}

type Filter = 'all' | 'high' | 'average' | 'warning' | 'unacked';

const FILTERS: { id: Filter; label: string; test: (p: Problem) => boolean }[] = [
  { id: 'all', label: 'Все', test: () => true },
  { id: 'high', label: 'High', test: (p) => p.sev >= 4 },
  { id: 'average', label: 'Average', test: (p) => p.sev === 3 },
  { id: 'warning', label: 'Warning', test: (p) => p.sev === 2 },
  { id: 'unacked', label: 'Без подтверждения', test: (p) => !p.acked },
];

const GRID = 'grid grid-cols-[6rem_minmax(0,1fr)_9rem_4.5rem_18rem] items-center gap-4 px-4';
const SKELETON = [
  { width: 'w-16' },
  { width: 'w-48' },
  { width: 'w-24' },
  { width: 'w-10', align: 'right' as const },
  { width: 'w-32' },
];

/** Строка INC-2189 — цель «Нужен ответ» в списке: тело садится на таблицу, контур — у строки (R36). */
const INCIDENT_ANCHOR = { role: 'item', label: 'INC-2189 · строка списка проблем' } as const;

export function InfraProblems({ preset = 'all' }: { preset?: Filter }) {
  const { dense } = usePrefs();
  const { toast } = useToast();
  const open = useObjectOpen();
  const rootRef = useRef<HTMLDivElement>(null);
  const incidentRef = useObserverAnchor<HTMLDivElement>('obj.INC-2189', INCIDENT_ANCHOR);
  const counter = useRef(0);
  const [items, setItems] = useState<Problem[]>(() => seed.map((p) => ({ ...p })));
  const [filter, setFilter] = useState<Filter>(preset);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(0);
  const [live, setLive] = useState(true);
  // Первая загрузка — сетка той же формы, что и список (R31).
  const loading = useViewLoading();

  // Живой поток: новая проблема приезжает сверху (R35).
  useEffect(() => {
    if (!live) return;
    const id = window.setInterval(() => {
      const a = ARRIVALS[counter.current % ARRIVALS.length];
      counter.current += 1;
      const scroller = rootRef.current?.closest<HTMLElement>('.overflow-y-auto');
      const keep = scroller && scroller.scrollTop > 2;
      const pid = `new-${counter.current}`;
      setItems((prev) => [
        {
          id: pid,
          sev: a.sev,
          name: a.name,
          target: { kind: a.kind, name: a.target },
          since: 'сейчас',
          acked: false,
          fresh: true,
        },
        ...prev,
      ]);
      // Подсветка проигрывается один раз: иначе она повторится при возвращении на вкладку.
      window.setTimeout(
        () => setItems((prev) => prev.map((p) => (p.id === pid ? { ...p, fresh: false } : p))),
        2700,
      );
      if (scroller && keep) window.requestAnimationFrame(() => (scroller.scrollTop += dense ? 40 : 52));
    }, 20000);
    return () => window.clearInterval(id);
  }, [live, dense]);

  const shown = items
    .filter((p) => FILTERS.find((f) => f.id === filter)!.test(p) || p.closed)
    .filter((p) => matchesQuery(`${p.name} ${p.target.name}`, query));

  const ack = (p: Problem) => {
    setItems((prev) => prev.map((x) => (x.id === p.id ? { ...x, acked: true } : x)));
    toast({
      title: `Подтверждено: ${p.name}`,
      action: {
        label: 'Отменить',
        undo: true,
        onClick: () => setItems((prev) => prev.map((x) => (x.id === p.id ? { ...x, acked: false } : x))),
      },
    });
  };
  const mute = (p: Problem) => {
    setItems((prev) => prev.map((x) => (x.id === p.id ? { ...x, muted: true } : x)));
    toast({
      title: `Заглушено на час: ${p.target.name}`,
      action: {
        label: 'Отменить',
        undo: true,
        onClick: () => setItems((prev) => prev.map((x) => (x.id === p.id ? { ...x, muted: false } : x))),
      },
    });
  };
  const close = (p: Problem) => {
    // Закрытая остывает несколько секунд (R34) и только потом уходит из списка.
    setItems((prev) => prev.map((x) => (x.id === p.id ? { ...x, closed: true } : x)));
    window.setTimeout(() => setItems((prev) => prev.filter((x) => x.id !== p.id)), 8000);
  };

  // Клавиши по физическим кнопкам — работают и в русской раскладке. Только на видимой вкладке.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const root = rootRef.current;
      if (!root || root.offsetParent === null || e.ctrlKey || e.metaKey || e.altKey) return;
      const t = e.target as HTMLElement;
      if (t.closest('input, textarea, [contenteditable="true"]')) return;
      const cur = shown[selected];
      if (e.code === 'KeyJ') setSelected((i) => Math.min(shown.length - 1, i + 1));
      else if (e.code === 'KeyK') setSelected((i) => Math.max(0, i - 1));
      else if (e.code === 'KeyA' && cur && !cur.acked) ack(cur);
      else if (e.code === 'KeyS' && cur && !cur.muted) mute(cur);
      else if (e.code === 'Enter' && cur) open(at(`/apps/infra/incident/INC-${2189 + shown.indexOf(cur)}`));
      else return;
      e.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const counts = (f: Filter) =>
    items.filter((p) => !p.closed && FILTERS.find((x) => x.id === f)!.test(p)).length;

  return (
    <Page>
      <div ref={rootRef} />
      <PageHeader
        title="Проблемы"
        meta={
          <>
            <span>обновлено 12 с назад</span>
            <span>J / K — по строкам · Alt — подсказки клавиш</span>
          </>
        }
        actions={<Switch size="sm" checked={live} onCheckedChange={setLive} label="Живой поток" />}
      />
      <Toolbar>
        <div className="w-64">
          <Input
            leadingIcon={<Search />}
            placeholder="Фильтр по тексту"
            aria-label="Фильтр по тексту"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        {FILTERS.map((f) => (
          <Chip key={f.id} selected={filter === f.id} count={counts(f.id)} onClick={() => setFilter(f.id)}>
            {f.label}
          </Chip>
        ))}
      </Toolbar>
      <Body className="pt-4">
        <div data-observer-block="" className="overflow-hidden rounded-lg border border-line bg-card">
          <div
            className={cn(
              GRID,
              'h-10 border-b border-line text-2xs font-semibold tracking-wider text-fg-subtle uppercase',
            )}
          >
            <span>Важность</span>
            <span>Проблема</span>
            <span>Объект</span>
            <span className="text-right">Длится</span>
            <span>Действия</span>
          </div>
          {loading ? (
            <TableSkeleton
              columns={SKELETON}
              rows={5}
              rowHeight={dense ? 'md' : 'lg'}
              header={false}
              className="px-4"
            />
          ) : (
            shown.map((p, i) => (
              <div
                key={p.id}
                ref={i === 0 ? incidentRef : undefined}
                onClick={() => setSelected(i)}
                className={cn(
                  GRID,
                  'border-b border-line last:border-b-0',
                  dense ? 'h-10' : 'h-12',
                  i === selected && 'bg-hover',
                  p.muted && 'opacity-60',
                  p.fresh && 'animate-arrive motion-reduce:animate-none',
                  p.closed && 'animate-cool-down motion-reduce:animate-none',
                )}
              >
                <span
                  className={cn('w-fit rounded-xs px-2 py-0.5 text-2xs font-semibold', severityPill[p.sev])}
                >
                  {severityLabels[p.sev]}
                </span>
                <span className="flex min-w-0 flex-col">
                  <ObjectLink
                    to={at(`/apps/infra/incident/INC-${2189 + i}`)}
                    className="truncate text-sm font-medium text-fg hover:underline"
                  >
                    {p.name}
                  </ObjectLink>
                  {p.closed && <span className="text-2xs text-fg-subtle">закрыта только что</span>}
                </span>
                <span className="truncate text-sm">
                  <EntityLink
                    to={at(
                      p.target.kind === 'host'
                        ? `/apps/infra/node/${p.target.name}`
                        : `/apps/infra/node/host-338`,
                    )}
                    kind={p.target.kind}
                    name={p.target.name}
                    subtitle={p.target.kind === 'host' ? 'Гипервизор' : 'Виртуальная машина'}
                    state={{
                      label: p.sev >= 4 ? 'Проблема' : 'Работает',
                      variant: p.sev >= 4 ? 'urgent' : 'success',
                    }}
                  />
                </span>
                <span className="text-right text-xs text-fg-muted tabular-nums">{p.since}</span>
                <span className="flex items-center gap-1">
                  {p.closed ? null : p.acked ? (
                    <span className="inline-flex items-center gap-1 text-xs text-success-fg">
                      <Check className="size-3.5" />
                      Подтверждено
                    </span>
                  ) : (
                    <Button size="sm" variant="ghost" onClick={() => ack(p)}>
                      Подтвердить
                      {i === selected && <ActionKey combo="A" />}
                    </Button>
                  )}
                  {!p.closed && !p.muted && (
                    <Button size="sm" variant="ghost" aria-label="Заглушить на час" onClick={() => mute(p)}>
                      <BellOff />
                      {i === selected && <ActionKey combo="S" />}
                    </Button>
                  )}
                  {!p.closed && (
                    <HoldToConfirm size="sm" hint="держите" onConfirm={() => close(p)}>
                      Закрыть
                    </HoldToConfirm>
                  )}
                </span>
              </div>
            ))
          )}
        </div>
      </Body>
    </Page>
  );
}
